import { useState, useEffect, useRef } from 'react';
import { Conversation } from '@11labs/client';
import { CircleStop, PhoneOff, PhoneOutgoing, Play } from 'lucide-react';
import { useSample } from '../SampleContext';
import { useLayout } from '../LayoutContext';

interface VideoRecorderProps {
    setScreen: (screen: 'intro' | 'recorder' | 'uploader' | 'webcam' | 'form') => void;
}


// Utility function to validate CSS hex color
const isValidHexColor = (color: string): boolean => {
    return /^#([0-9A-F]{3}){1,2}$/i.test(color);
};


const VideoRecorder: React.FC<VideoRecorderProps> = ({ setScreen }) => {
    const { setRecordedBlob, setSampleDuration } = useSample();
    const { layout } = useLayout();
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const [isRecording, setIsRecording] = useState<boolean>(false);
    const [videoDuration, setVideoDuration] = useState<number>(0);
    const isRecordingRef = useRef(isRecording);
    const conversationRef = useRef<any>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const recordingStartTimeRef = useRef<number | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const durationIntervalRef = useRef<number | null>(null);
    const recordedBlobRef = useRef<Blob | null>(null);


    // Fallback to default colors if invalid
    const safeColors = {
        buttons: isValidHexColor(layout.color.buttons) ? layout.color.buttons : '#22FFAF',
        text: isValidHexColor(layout.color.text) ? layout.color.text : '#000000',
        hover: isValidHexColor(layout.color.hover) ? layout.color.hover : '#1DE9A3',
    };

    const connectedSVG = <CircleStop color={safeColors.text} />;
    const defaultSVG = <Play color={safeColors.text} />;

    const processMediaBlob = async (blob: Blob) => {
        if (!recordingStartTimeRef.current) {
            setRecordedBlob(null);
            setScreen('form');
            return;
        }

        try {
            const duration = (Date.now() - recordingStartTimeRef.current) / 1000;
            if (duration <= 0.001) {
                setRecordedBlob(null);
                setScreen('form');
                return;
            }

            recordedBlobRef.current = blob;
            setRecordedBlob(blob);
            setSampleDuration(Number(duration.toFixed(1)));
            setScreen('form');


            setVideoDuration(0);
        } catch (error) {
            console.error('Error processing media blob:', error);
            setRecordedBlob(null);
            setScreen('form');
            setVideoDuration(0);
        }
    };

    const toggleRecording = async () => {
        if (!isConnected) {
            try {
                // Request audio and video permissions
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: true,
                    video: { width: 1280, height: 720 },
                });
                mediaStreamRef.current = stream;

                // Set up video display
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play();
                }

                const tracks = stream.getTracks();
                if (tracks.length === 0 || !tracks.some((track) => track.enabled)) {
                    alert('No audio or video tracks available.');
                    return;
                }

                mediaRecorderRef.current = new MediaRecorder(stream, {
                    mimeType: 'video/webm;codecs=vp8,opus',
                });
                const mediaChunks: Blob[] = [];
                mediaRecorderRef.current.ondataavailable = (event: BlobEvent) => {
                    if (event.data.size > 0) {
                        mediaChunks.push(event.data);
                    }
                };
                mediaRecorderRef.current.onstop = async () => {
                    const blob = new Blob(mediaChunks, { type: 'video/webm' });
                    await processMediaBlob(blob);
                };

                mediaRecorderRef.current.onstart = () => {
                    recordingStartTimeRef.current = Date.now();
                    setIsRecording(true);
                    setVideoDuration(0);

                    // Update duration every 100ms
                    durationIntervalRef.current = setInterval(() => {
                        if (recordingStartTimeRef.current) {
                            const duration = (Date.now() - recordingStartTimeRef.current) / 1000;
                            setVideoDuration(Number(duration.toFixed(1)));
                            setSampleDuration(Number(duration.toFixed(1)));
                        }
                    }, 100) as unknown as number;
                };

                conversationRef.current = await Conversation.startSession({
                    agentId: 'eJO0Wgy4ksko99fLKWvY',
                    onConnect: () => {
                        setIsConnected(true);
                        mediaRecorderRef.current?.start();
                    },
                    onDisconnect: () => {
                        setIsConnected(false);
                        mediaRecorderRef.current?.stop();
                        cleanupMedia();
                    },
                    onModeChange: (mode: any) => {
                        // Placeholder for potential agent speaking indicator
                    },
                });
            } catch (error) {
                console.error('Error starting recording:', error);
                alert('Failed to access camera and microphone. Please check permissions.');
            }
        } else {
            if (conversationRef.current) {
                await conversationRef.current.endSession();
                conversationRef.current = null;
            }
        }
    };

    const cleanupMedia = () => {
        if (durationIntervalRef.current) {
            clearInterval(durationIntervalRef.current);
            durationIntervalRef.current = null;
        }
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach((t) => t.stop());
            mediaStreamRef.current = null;
        }
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
        setIsRecording(false);
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    };

    useEffect(() => {
        isRecordingRef.current = isRecording;
    }, [isRecording]);

    useEffect(() => {
        return () => {
            cleanupMedia();
        };
    }, []);

    const formatDuration = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div
            className={`relative w-full max-w-[375px] p-6 rounded-xl bg-black shadow-[0_4px_20px_rgba(0,0,0,0.5)] flex flex-col items-center`}
        >
            <button
                className={`absolute left-[27px] bottom-[24px] border-none rounded-lg px-[12px] py-[6px] font-bold flex items-center gap-3 text-base cursor-pointer bg-[${safeColors.buttons}] hover:bg-[${safeColors.hover}] text-[${safeColors.text}]`}
                onClick={() => {
                    setScreen('intro');
                    setRecordedBlob(null);
                    setSampleDuration(0);
                }}
            >
                Back
            </button>
            <h2 className={`text-[30px] font-bold text-[${safeColors.buttons}] mb-5`}>Voice and Video Recorder</h2>
            <div className="text-white mb-6 text-center">
                {isConnected
                    ? 'Connect to our Agent to start recording your voice and video. Follow the agent\'s instructions to complete your sample.'
                    : 'Make sure you are well lit and do NOT cover your face'}
            </div>
            <div className="w-full h-[200px] mb-6 flex justify-center items-center bg-[#1A1A1A] rounded-md overflow-hidden">
                <video
                    ref={videoRef}
                    autoPlay
                    muted
                    className="w-full h-full object-cover"
                />
            </div>
            <button
                className={`w-[60px] h-[60px] border-none rounded-full flex justify-center items-center cursor-pointer transition-colors shadow-[0_4px_8px_rgba(0,0,0,0.1)] ${isConnected ? `bg-[${safeColors.buttons}] hover:bg-[${safeColors.hover}]` : `bg-[${safeColors.buttons}] hover:bg-[${safeColors.hover}]`
                    } text-[${safeColors.text}]`}
                onClick={toggleRecording}
            >
                {isConnected ? connectedSVG : defaultSVG}
            </button>
            {isConnected && (
                <div
                    className={`${videoDuration < 60 ? 'text-red-500' : videoDuration <= 300 ? 'text-yellow-500' : 'text-green-500'
                        } mt-4 text-lg`}
                >
                    Recorded: {formatDuration(videoDuration)}
                </div>
            )}
        </div>
    );
};

export default VideoRecorder;